import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { stripEmojiUnicode } from '../../utils/stripEmoji';
import { BookingStepProgress } from './BookingStepProgress';
import './BookingComponents.css';
import './booking-brand.css';

interface Service {
  id: string;
  name: string;
  description?: string;
  duration?: string | number; // Puede ser "00:45:00" o número en minutos
  price?: string | number;
  currency?: string;
  /** Sección del catálogo ("Primera vez", "Sesiones"…). Opcional: sin ella, lista plana. */
  category?: string | null;
}

interface ServiceSelectionProps {
  services: Service[];
  onSelect: (serviceId: string) => void;
  language?: string;
  i18n?: { title?: string; message?: string; selectButtonLabel?: string };
  /** Tarjeta de packs al pie ("Pack 10 sesiones · 100 €"). Se oculta si no hay packs. */
  bundleTeaser?: { label: string; hint?: string; badge?: string } | null;
  onOpenBundles?: () => void;
}

function formatDuration(duration: string | number): string {
  if (typeof duration === 'number') {
    return `${duration} min`;
  }
  // Formato "00:45:00" -> "45 min"
  const parts = duration.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes} min`;
  }
  return duration;
}

function formatPrice(price: string | number | undefined, currency?: string): string {
  if (price === undefined || price === null || price === '') return '';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (!Number.isFinite(num)) return '';
  const curr = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num} ${curr}`;
  }
}

function stripMarkdown(text: string): string {
  return text.replace(/\*+/g, '').trim();
}

/** Agrupa por categoría respetando el orden de aparición; sin categorías devuelve un solo grupo. */
function groupByCategory(services: Service[]): Array<{ label: string | null; items: Service[] }> {
  const groups: Array<{ label: string | null; items: Service[] }> = [];
  const index = new Map<string, number>();
  for (const service of services) {
    const raw = typeof service.category === 'string' ? service.category.trim() : '';
    const key = raw || '__none__';
    const existing = index.get(key);
    if (existing != null) {
      groups[existing].items.push(service);
    } else {
      index.set(key, groups.length);
      groups.push({ label: raw || null, items: [service] });
    }
  }
  return groups;
}

export function ServiceSelection({
  services,
  onSelect,
  language = 'es',
  i18n,
  bundleTeaser,
  onOpenBundles,
}: ServiceSelectionProps) {
  const { t } = useTranslation();
  const tl = (key: string, opts?: Record<string, unknown>) => t(key, { lng: language, ...(opts || {}) });
  const rawTitle = (i18n?.title || i18n?.message) ? stripMarkdown(i18n.title || i18n.message || '') : '';
  const heading = stripEmojiUnicode(rawTitle) || tl('booking.step1_title');
  const groups = groupByCategory(services);
  // Solo se pintan cabeceras de sección cuando el catálogo está realmente categorizado.
  const showSectionLabels = groups.some((g) => g.label);

  return (
    <div className="booking-step booking-step--service-select bk-step">
      <BookingStepProgress current={1} stepLabel={tl('booking.step_of', { current: 1, total: 3 })} title={heading} />

      <div className="bk-service-groups">
        {groups.map((group, groupIndex) => (
          <section key={group.label ?? `group-${groupIndex}`} className="bk-service-group">
            {showSectionLabels && group.label ? (
              <div className="bk-section-label">
                <span>{stripEmojiUnicode(group.label)}</span>
                <i aria-hidden />
              </div>
            ) : null}

            <div
              className={`bk-service-card${groupIndex === 0 && showSectionLabels ? ' bk-service-card--accent' : ''}`}
            >
              {group.items.map((service) => {
                const meta = [
                  service.duration ? formatDuration(service.duration) : '',
                  formatPrice(service.price, service.currency),
                  service.description ? stripEmojiUnicode(service.description) : '',
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <button
                    key={service.id}
                    type="button"
                    className="bk-service-row"
                    onClick={() => onSelect(service.id)}
                  >
                    <span className="bk-service-row-text">
                      <span className="bk-service-row-name">{stripEmojiUnicode(service.name)}</span>
                      {meta ? <span className="bk-service-row-meta">{meta}</span> : null}
                    </span>
                    <ChevronRight className="bk-chevron" size={16} strokeWidth={2.2} aria-hidden />
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {bundleTeaser && onOpenBundles ? (
        <button type="button" className="bk-bundle-teaser" onClick={onOpenBundles}>
          {bundleTeaser.badge ? <span className="bk-bundle-teaser-badge">{bundleTeaser.badge}</span> : null}
          <span className="bk-bundle-teaser-text">
            <span className="bk-bundle-teaser-title">{bundleTeaser.label}</span>
            {bundleTeaser.hint ? <span className="bk-bundle-teaser-hint">{bundleTeaser.hint}</span> : null}
          </span>
          <ChevronRight className="bk-chevron" size={16} strokeWidth={2.2} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
